import { createContext, useContext } from 'react';
import type { SingleNotificationToastProps } from '@rapid-recovery-agency-inc/sloth-ui-web';
import type { InsighttNotificationType } from '../../components/notifications/types';

export type PushNotification = (props: SingleNotificationToastProps) => void;

let activePush: PushNotification | null = null;

interface LegacyNotificationBridge {
  create: (notification: InsighttNotificationType) => void;
  remove: (id: string) => void;
  list: () => InsighttNotificationType[];
}

let activeLegacyBridge: LegacyNotificationBridge | null = null;
const legacyChangeListeners = new Set<() => void>();

export function createLegacyNotification(notification: InsighttNotificationType): void {
  activeLegacyBridge?.create(notification);
}

export function removeLegacyNotification(id: string): void {
  activeLegacyBridge?.remove(id);
}

export function listLegacyNotifications(): InsighttNotificationType[] {
  return activeLegacyBridge?.list() ?? [];
}

export function subscribeLegacyNotificationChange(listener: () => void): () => void {
  legacyChangeListeners.add(listener);
  return () => legacyChangeListeners.delete(listener);
}

export function notifyLegacyNotificationChange(): void {
  legacyChangeListeners.forEach((listener) => listener());
}

export function registerLegacyNotificationBridge(bridge: LegacyNotificationBridge): () => void {
  activeLegacyBridge = bridge;
  notifyLegacyNotificationChange();
  return () => {
    if (activeLegacyBridge === bridge) {
      activeLegacyBridge = null;
      notifyLegacyNotificationChange();
    }
  };
}

/** Show a local toast while NotificationsProvider is mounted; otherwise this is a no-op. */
export function pushNotification(props: SingleNotificationToastProps): void {
  activePush?.(props);
}

/** The provider registers before descendant layout and passive effects run. */
export function registerNotificationPush(push: PushNotification): () => void {
  activePush = push;
  return () => {
    if (activePush === push) activePush = null;
  };
}

export interface NotificationsContextValue {
  pushNotification: PushNotification;
}

export const NotificationsContext = createContext<NotificationsContextValue>({ pushNotification });

/** Access the current provider's local toast action from a React descendant. */
export function useNotificationsContext(): NotificationsContextValue {
  return useContext(NotificationsContext);
}
